/* 在真實瀏覽器的 IndexedDB 上跑一遍完整生命週期。
   單元測試用 fake-indexeddb 證明邏輯；這裡證明真環境也能跑。
   網址加 ?selftest=1 就會執行，結果寫進畫面（正式版不會載入這條路徑）。 */
import { openRepository } from "../data/repository.ts";
import { DAY_MS } from "../domain/rules.ts";
import { UNCATEGORIZED_ID } from "../domain/seed.ts";

export async function runSelfTest(): Promise<string[]> {
  const out: string[] = [];
  const name = "lifesage-selftest-" + Date.now();
  const check = (label: string, ok: boolean, detail = "") => {
    out.push((ok ? "PASS " : "FAIL ") + label + (detail ? "　→ " + detail : ""));
  };
  try {
    let clock = 1_000 * DAY_MS;
    const repo = await openRepository(indexedDB, "selftest-device", () => clock, name);

    const cats = await repo.listCategories();
    check("init 種下 14 個類別", cats.length === 14, "實際 " + cats.length);

    const site = cats.find((c) => c.name === "生命科學") ?? cats[0]!;
    const created = await repo.saveConcept(null, {
      summary: "粒線體是細胞的能量工廠，擁有自己獨立的 DNA。",
      note: "高中生物只說它是發電廠，這裡補上內共生起源與母系遺傳的原因。",
      source: "分子生物學 第 7 章", primaryCategoryId: site.id,
    });
    check("建立知識 → rev 1", created.concept.rev === 1, "rev=" + created.concept.rev);
    check("建立時寫入一筆 op", (await repo.listOps(created.concept.id)).length === 1);

    const edited = await repo.saveConcept(created.concept, { summary: "改過的摘要。" });
    check("修改 → rev 2 且內容更新", edited.concept.rev === 2 && edited.concept.summary === "改過的摘要。");

    const noop = await repo.saveConcept(edited.concept, { summary: "改過的摘要。" });
    check("沒有實際改動 → 不產生 op", noop.op === null && noop.concept.rev === 2);

    const restored = await repo.restoreConcept(created.concept.id, 1);
    check("回復到第 1 版", restored.summary === created.concept.summary && restored.rev === 3,
      "rev=" + restored.rev + " 摘要=" + restored.summary.slice(0, 8));

    const hist = await repo.history(created.concept.id);
    check("歷史只增不減", hist.length >= 3 && hist[0]!.rev === 3, "共 " + hist.length + " 筆");

    await repo.setConceptStatus(created.concept.id, "trashed");
    check("軟刪除後不在一般列表", (await repo.listConcepts()).length === 0);
    check("軟刪除後仍在垃圾桶列表", (await repo.listConcepts(true)).length === 1);

    const s = await repo.saveSettings({ sheetMaterial: "day", adultUnlocked: true });
    check("設定存得住", s.sheetMaterial === "day" && (await repo.getSettings()).adultUnlocked === true);

    const orphan = await repo.saveConcept(null, { summary: "孤兒測試。", note: "用來確認未分類可以被指定。" });
    check("未分類類別存在", (await repo.listCategories()).some((c) => c.id === UNCATEGORIZED_ID)
      && orphan.concept.primaryCategoryId === UNCATEGORIZED_ID);

    repo.close();
  } catch (e) {
    out.push("FAIL 執行期間丟出例外　→ " + String(e));
  } finally {
    try { indexedDB.deleteDatabase(name); } catch { /* 清不掉就算了 */ }
  }
  const failed = out.filter((l) => l.startsWith("FAIL")).length;
  out.push(failed === 0 ? "全部通過（" + out.length + " 項）" : failed + " 項失敗");
  return out;
}
